import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getExams } from "../../../services/ExamService";
import { getExamSessions } from "../../../services/ExamSessionService";
import { getResults } from "../../../services/ExamResultService";
import { getStudentProfiles } from "../../../services/StudentProfileService";
import { useTranslation } from "react-i18next";
import { getLocale, lazyLabels } from "../../../utils/i18nLabels";

const statusLabels = lazyLabels({ ongoing: "common.examSessionStatus.ongoing", scheduled: "common.examSessionStatus.scheduled" });
const statusColors = { ongoing: "primary", scheduled: "warning" };

function TeacherDashboard() {
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.auth?.user);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [myExams, setMyExams] = useState(new Map());
  const [mySessions, setMySessions] = useState([]);
  const [myResults, setMyResults] = useState([]);
  const [studentLabels, setStudentLabels] = useState(new Map());

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const examsRes = await getExams({ limit: 500 });
        const allExams = examsRes.data?.data || [];
        const examsMap = new Map(
          allExams
            .filter((exam) => exam.created_by === user?.id)
            .map((exam) => [exam.id, exam]),
        );
        setMyExams(examsMap);

        const [ongoingRes, scheduledRes] = await Promise.all([
          getExamSessions({ limit: 500, status: "ongoing" }),
          getExamSessions({ limit: 500, status: "scheduled" }),
        ]);
        const allSessions = [
          ...(ongoingRes.data?.data || []),
          ...(scheduledRes.data?.data || []),
        ];
        const sessions = allSessions
          .filter((session) => examsMap.has(session.exam_id))
          .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
        setMySessions(sessions);

        try {
          const resultsRes = await getResults({ limit: 500 });
          const allResults = resultsRes.data?.data || [];
          const results = allResults
            .filter((result) => examsMap.has(result.exam_id))
            .sort((a, b) => b.score_percentage - a.score_percentage);
          setMyResults(results);

          if (results.length) {
            const profilesRes = await getStudentProfiles({ limit: 500 });
            const profiles = profilesRes.data?.data || [];
            setStudentLabels(
              new Map(
                profiles.map((profile) => [profile.id, profile.matricule]),
              ),
            );
          }
        } catch (resultsErr) {
          setMyResults([]);
        }
      } catch (err) {
        setLoadError(
          err?.response?.data?.message ||
            t("pages.teacherDashboard.loadError"),
        );
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const ongoingCount = useMemo(
    () => mySessions.filter((s) => s.status === "ongoing").length,
    [mySessions],
  );
  const scheduledCount = useMemo(
    () => mySessions.filter((s) => s.status === "scheduled").length,
    [mySessions],
  );
  const averageScore = useMemo(() => {
    if (!myResults.length) return null;
    const sum = myResults.reduce(
      (acc, r) => acc + Number(r.score_percentage || 0),
      0,
    );
    return Math.round(sum / myResults.length);
  }, [myResults]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t("common.crud.loading")}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="bg-welcome-banner mb-3">
          <div className="card-body">
            <h1 className="text-white fs-4 mb-1">{t("pages.teacherDashboard.greeting", { name: user?.email })}</h1>
          </div>
        </div>

        {loadError && <div className="alert alert-warning">{loadError}</div>}

        <div className="row">
          <div className="col-xl-3 col-sm-6 mb-3">
            <div className="card h-100">
              <div className="card-body d-flex align-items-center justify-content-between">
                <div>
                  <p className="mb-1 text-muted">{t("menu.myExams")}</p>
                  <h3 className="mb-0">{myExams.size}</h3>
                </div>
                <span className="avatar avatar-lg bg-primary-transparent rounded-circle d-flex align-items-center justify-content-center">
                  <i className="ti ti-clipboard-text text-primary fs-24"></i>
                </span>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-sm-6 mb-3">
            <div className="card h-100">
              <div className="card-body d-flex align-items-center justify-content-between">
                <div>
                  <p className="mb-1 text-muted">{t("common.ui.sessionsCours")}</p>
                  <h3 className="mb-0">{ongoingCount}</h3>
                </div>
                <span className="avatar avatar-lg bg-success-transparent rounded-circle d-flex align-items-center justify-content-center">
                  <i className="ti ti-broadcast text-success fs-24"></i>
                </span>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-sm-6 mb-3">
            <div className="card h-100">
              <div className="card-body d-flex align-items-center justify-content-between">
                <div>
                  <p className="mb-1 text-muted">{t("common.ui.scoreMoyen")}</p>
                  <h3 className="mb-0">
                    {averageScore !== null ? `${averageScore}%` : "-"}
                  </h3>
                </div>
                <span className="avatar avatar-lg bg-info-transparent rounded-circle d-flex align-items-center justify-content-center">
                  <i className="ti ti-chart-bar text-info fs-24"></i>
                </span>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-sm-6 mb-3">
            <div className="card h-100">
              <div className="card-body d-flex align-items-center justify-content-between">
                <div>
                  <p className="mb-1 text-muted">{t("pages.teacherDashboard.upcomingSessions")}</p>
                  <h3 className="mb-0">{scheduledCount}</h3>
                </div>
                <span className="avatar avatar-lg bg-warning-transparent rounded-circle d-flex align-items-center justify-content-center">
                  <i className="ti ti-calendar-event text-warning fs-24"></i>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4 className="mb-0 fs-18">{t("pages.teacherDashboard.mySessions")}</h4>
          </div>
          <div className="card-body">
            {mySessions.length > 0 ? (
              <div className="d-flex gap-3 overflow-auto pb-2">
                {mySessions.map((session) => (
                  <div
                    key={session.id}
                    className="bg-light rounded p-3"
                    style={{ minWidth: 220, flex: "0 0 auto" }}
                  >
                    <span
                      className={`badge badge-lg mb-2 badge-${statusColors[session.status]}`}
                    >
                      <i className="ti ti-clock me-1"></i>
                      {new Date(session.start_at).toLocaleString(getLocale())}
                    </span>
                    <p className="text-dark mb-1">
                      {myExams.get(session.exam_id)?.title || t("common.ui.examen")}
                    </p>
                    <small className="text-muted">
                      {statusLabels[session.status]}
                    </small>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted text-center py-4 mb-0">
                {t("pages.teacherDashboard.noSessions")}
              </p>
            )}
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h4 className="mb-0 fs-18">{t("pages.teacherDashboard.latestResults")}</h4>
              </div>
              <div className="card-body p-0">
                {myResults.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table mb-0">
                      <thead className="thead-light">
                        <tr>
                          <th>{t("roles.STUDENT")}</th>
                          <th>{t("common.ui.examen")}</th>
                          <th>{t("common.ui.score")}</th>
                          <th>{t("common.crud.statusColumn")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {myResults.slice(0, 8).map((result) => (
                          <tr key={result.id}>
                            <td>
                              {studentLabels.get(result.student_id) || "-"}
                            </td>
                            <td>{myExams.get(result.exam_id)?.title || "-"}</td>
                            <td>{Math.round(result.score_percentage)}%</td>
                            <td>
                              <span
                                className={`badge bg-${result.passed ? "success" : "danger"}`}
                              >
                                {result.passed ? t("pages.teacherDashboard.passed") : t("pages.teacherDashboard.failed")}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted text-center py-4 mb-0">
                    {t("pages.teacherDashboard.noResults")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TeacherDashboard;
